import {Button, Tabs, Tab, Divider} from '@mui/material';
import {ArrowLeftIcon} from '$assets/svgs';
import ClinicalDataContainer from './ClinicalData';
import {SyntheticEvent, useState, useEffect} from 'react';
import {PATIENT_DETAILS_TAB_LIST, PatientDetailTab} from './constant';
import PatientDetailNotes from './Notes';
import './styles.scss';
import OverviewContainer from './Overview';
import Timeline from './Timeline';
import {useLocation, useParams} from 'react-router-dom';
import CheckInTimer from '$modules/frontdesk/Calendar/BookingDetails/CheckInTimer';
import useAuthUser from '../../../../hooks/useAuthUser';
import {useConfirmConsultationOutcome} from './hooks/hooks';
import {usePatientDetailsHook} from './hooks/usePatientDetailsHook';
import EndConsultationModal from './EndConsultationModal';
import {CurrentAppointment} from './types';
import useAppointmentTimerSync from '../../../../hooks/useAppointmentTimerSync';
import {useQueryClient} from '@tanstack/react-query';
import PatientBreadcrumb from '~/modules/frontdesk/PatientList/PatientDetails/PatientBreadcrumb';

const EMPTY_APPOINTMENT: CurrentAppointment = {
  id: 0,
  check_in_time: '',
  check_out_time: '',
  is_end_consultation: false,
};

interface PatientDetailsComponentProps {
  patientId?: number;
  handleBackClick: () => void;
}

const PatientDetailsComponent: React.FC<PatientDetailsComponentProps> = ({
  patientId: propPatientId,
  handleBackClick,
}) => {
  const params = useParams();
  const patientId =
    propPatientId ?? (params.patientId ? Number(params.patientId) : undefined);

  const location = useLocation();
  const activeSubTab = location?.state?.activeSubTab;
  const [activeTab, setActiveTab] = useState<PatientDetailTab>(
    activeSubTab || PatientDetailTab.Overview
  );
  const [overviewSelectedNoteId, setOverviewSelectedNoteId] = useState<
    number | null
  >(null);

  const {data: patientDetails} = usePatientDetailsHook(patientId!);
  const queryClient = useQueryClient();
  useAppointmentTimerSync(patientDetails?.data.current_appointment?.id, () => {
    if (patientId) {
      queryClient.invalidateQueries({queryKey: ['patient-details', patientId]});
    }
    if (patientDetails?.data.current_appointment?.id) {
      queryClient.invalidateQueries({
        queryKey: [
          'appointment-detail',
          patientDetails.data.current_appointment.id,
        ],
      });
    }
  });
  const startTime = patientDetails?.data.current_appointment?.check_in_time;
  const showTimer = !!startTime;
  const rawUser = useAuthUser();
  const user = rawUser
    ? {...rawUser, hospitalId: rawUser.hospitalId ?? 0}
    : {id: 0, hospitalId: 0};
  const [bookingDetails, setBookingDetails] = useState<CurrentAppointment>(
    patientDetails?.data.current_appointment ?? EMPTY_APPOINTMENT
  );

  useEffect(() => {
    if (patientDetails?.data.current_appointment) {
      setBookingDetails(patientDetails.data.current_appointment);
    }
  }, [patientDetails?.data.current_appointment]);

  const [endConsultationModalOpen, setEndConsultationModalOpen] =
    useState(false);
  const [outcomeForm, setOutcomeForm] = useState({
    description: '',
    surgery_listed: false,
    follow_up_required: false,
  });

  const {sendConfirmConsultationOutcomeRequest, isPending, disableSubmit} =
    useConfirmConsultationOutcome(() => {
      setEndConsultationModalOpen(false);
      setOutcomeForm({
        description: '',
        surgery_listed: false,
        follow_up_required: false,
      });
      setBookingDetails(prev => ({...prev, is_end_consultation: true}));
    }, patientDetails?.data.current_appointment?.id || 0);

  const handleTabChange = (_ev: SyntheticEvent, value: PatientDetailTab) => {
    setActiveTab(value);
  };

  const tabs = PATIENT_DETAILS_TAB_LIST.map(({label, value}) => (
    <Tab key={value} label={label} value={value} />
  ));

  let tabContent: React.ReactNode = null;
  switch (activeTab) {
    case PatientDetailTab.Overview:
      tabContent = (
        <OverviewContainer
          setActiveTab={setActiveTab}
          setOverviewSelectedNoteId={setOverviewSelectedNoteId}
        />
      );
      break;
    case PatientDetailTab.ClinicalData:
      tabContent = <ClinicalDataContainer />;
      break;
    case PatientDetailTab.Timeline:
      tabContent = <Timeline />;
      break;
    case PatientDetailTab.Notes:
      tabContent = (
        <PatientDetailNotes overviewSelectedNoteId={overviewSelectedNoteId} />
      );
      break;
  }

  let endConsultationModal = null;
  if (endConsultationModalOpen) {
    endConsultationModal = (
      <EndConsultationModal
        open={endConsultationModalOpen}
        handleClose={setEndConsultationModalOpen}
        bookingId={bookingDetails.id}
        user={user}
        patientId={patientId!}
        outcomeForm={outcomeForm}
        setOutcomeForm={setOutcomeForm}
        sendConfirmConsultationOutcomeRequest={
          sendConfirmConsultationOutcomeRequest
        }
        isPending={isPending}
        disableSubmit={disableSubmit}
      />
    );
  }

  return (
    <div className="patient-details">
      <header className="patient-details__header patient-details__header--relative">
        <div
          className="patient-details__header__navigation patient-details__header__navigation--space-between"
          style={{display: 'flex', alignItems: 'center'}}
        >
          <Button
            startIcon={<ArrowLeftIcon />}
            className="patient-details__button--back"
            onClick={handleBackClick}
          >
            Back
          </Button>

          {showTimer ? (
            <div className="patient-details__header__divider-with-timer">
              <div
                className={`patient-details__header__divider-left${showTimer ? ' with-timer' : ''}`}
              ></div>
              <div className="patient-details__header__timer-absolute">
                <CheckInTimer
                  appointmentId={
                    patientDetails?.data.current_appointment?.id || 0
                  }
                  startTime={startTime || ''}
                  onConsultantEndConsultation={() =>
                    setEndConsultationModalOpen(true)
                  }
                  disabled={
                    !!bookingDetails.is_end_consultation ||
                    isPending ||
                    disableSubmit
                  }
                />
              </div>
              <div
                className={`patient-details__header__divider-right${showTimer ? ' with-timer' : ''}`}
              ></div>
            </div>
          ) : (
            <Divider className="mt-3 mx-10" />
          )}

          {patientDetails?.data?.full_name && (
            <div style={{marginLeft: 'auto', marginRight: '16px'}}>
              <PatientBreadcrumb
                patientName={patientDetails?.data?.full_name}
              />
            </div>
          )}
        </div>

        <div className="patient-details__header__tab-bar">
          <Tabs value={activeTab} onChange={handleTabChange}>
            {tabs}
          </Tabs>
        </div>
      </header>
      <div className="patient-details__body overflow-auto">{tabContent}</div>
      {endConsultationModal}
    </div>
  );
};

export default PatientDetailsComponent;
